import { Component, EventEmitter, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { PgTitleComponent } from 'src/app/shared/components/title-bar/title-bar.component';
import { forkJoin } from 'rxjs';
import { DateTimeHelpers } from 'src/app/shared/helpers/date-time.helpers';
import { OrganisationService } from 'src/app/organisation/organisation.service';
import { Organisation } from 'src/app/generated/services/organisations/organisation';
import _ from 'lodash';
import { saveAs } from 'file-saver';
import moment from 'moment';
import { DocumentService } from 'src/app/documents/documents.service';
import { SharedModule } from 'src/app/shared/shared.module';
import { SummeryReportToolbarComponent } from './fleet-summery-report-toolbar/summery-report-toolbar/fleet-summery-report-toolbar.component';
import { NoonReportServiceSpa } from 'src/app/generated/services/transportation/reports/noonSpa';
import { NoonReportV202005 } from 'src/app/generated/services/transportation/reports/noon';
import { Vessel } from 'src/app/generated/services/transportation/vessels/vessel';
import { Voyage } from 'src/app/generated/services/transportation/voyages/voyage';

@Component({
  selector: 'app-fleet-summery-report',
  templateUrl: './fleet-summery-report.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [SharedModule,]
})
export class FleetSummeryReportComponent implements OnInit, PgTitleComponent {
  title = new EventEmitter<string>();

  toolbarComponentType = SummeryReportToolbarComponent;
  toolbarComponent!: SummeryReportToolbarComponent;

  reports: any[] = [];
  organisations: Organisation[] = [];
  constructor(
    private _organisationService: OrganisationService,
    private _documentService: DocumentService,
    private _noonReportService: NoonReportServiceSpa
  ) { }

  ngOnInit() {
    this.title.emit('Vessel Management report');
    forkJoin(
      [
        this._noonReportService.getLatestManagementReport({}),
        this._organisationService.get()
      ]
    ).subscribe(results => {
      this.reports = results[0].reports
        .sort((a, b) =>(a.voyage?.charterer ?? '').localeCompare(b.voyage?.charterer ?? ''));
      this.organisations = results[1];
    });
  }

  formatDate(timestamp: number) {
    if (timestamp > 0) {
      return DateTimeHelpers.formatUTCDate(timestamp);
    } else {
      return '';
    }
  }

  organisationFormatter(id: string) {
    const name = id ? this.organisations.find(o => o.name === id)!.nameTranslations['en'] : '';
    if (name) {
      return name.substring(0, 8);
    }
  }

  vesselAge(v: Vessel) {
    return moment().diff(moment(new Date(v.spec?.yearBuilt || 0, 1, 1)), 'years');
  }

  inssurance(vessel: Vessel) {
    return vessel.certificates.find(c => c.name.includes('P&I'));
  }

  formatCargoOnboard(report: NoonReportV202005) {
    return _.chain(report.tanksCondition)
      .groupBy(r => r.product)
      .map((items, key) => {
        const sum = Math.round(items.map(i => i.quantity).reduce((p, c) => p + c, 0));
        if (sum > 0) {
          return `${key[0]}: ${sum}`;
        }
      })
      .value().filter(v => v !== undefined).join(', ');
  }

  blFormatter(voyage: Voyage, laden: boolean) {
    const bl = ((voyage.billOfLadings.filter(b => b.revise).length > 0) ?
      voyage.billOfLadings.filter(b => b.revise) :
      voyage.billOfLadings.filter(b => !b.revise)).sort((a, b) => b.date - a.date)[0];
    if (bl && laden) {
      return this.formatDate1(bl.date, bl.loadingPortOrganisationId);
    }
  }

  formatDate1(dt: number, portOrganisationId: string) {
    if (this.organisations) {
      const selectedTimezone = this._organisationService.getOrganisationTimezone(portOrganisationId, this.organisations);
      if (selectedTimezone) {
        return DateTimeHelpers.getDateFromTimestamp(selectedTimezone, dt);
      } else {
        console.error(`Cannot find timezone for organisation Id: ${portOrganisationId}`);
      }
    }
  }

  bunkerLastRecievedDate(report: NoonReportV202005, bunkerType: string) {
    return report.noonReportBunkerStatus!.lastReceived.find(l => l.receivedType === bunkerType)!.timestamp;
  }

  bunkerLastRecievedFormatter(report: NoonReportV202005, bunkerType: string) {
    return report.noonReportBunkerStatus!.lastReceived
      .filter(l => l.receivedType.includes(bunkerType))
      .map(n => n.quantityMt)
      .reduce((a, b) => a + b, 0)
      .toFixed(3);
  }

  robFormatter(report: NoonReportV202005, bunkerType: string) {
    return Math.round(report.noonReportBunkerStatus!.rob
      .filter(l => l.bunkerType.includes(bunkerType))
      .map(n => n.quantityMt)
      .reduce((a, b) => a + b, 0) * 1000) / 1000;
  }

  capitalize(text: string) {
    const temp = text.trim();
    return temp[0].toLocaleUpperCase() + temp.substring(1).toLocaleLowerCase();
  }

  windSeaFormatter(noonReport: NoonReportV202005) {
    return `${noonReport.condition!.windForce}
    ${noonReport.condition!.windForceDirection} /
    ${noonReport.condition!.seaForce}
    ${noonReport.condition!.seaForceDirection}`;
  }

  checkDryDockExpirey(vessel: Vessel) {
    if (vessel.spec!.dryDock) {
      const diff = moment(vessel.spec!.dryDock.nextDryDock).diff(moment(), 'days');
      if (diff > 90) {
        return 2;
      } else if (diff <= 90 && diff >= 0) {
        return 1;
      } else if (diff < 0) {
        return 0;
      }
    }
  }

  checkUpdatedNoon(noonreport: NoonReportV202005) {
    return moment(noonreport.date!.timestamp).isBefore(moment(), 'day');
  }

  checkEta(noonreport: NoonReportV202005) {
    return noonreport.voyageDetails!.eta < new Date().getTime();
  }

  downloadFile(report: NoonReportV202005) {
    this._documentService.download(report.fileId).subscribe(b => {
      const n = report.vesselName.split(' ').map(s => s.toLowerCase()).join('-');
      saveAs(b.body, `${n}-${DateTimeHelpers.formatLocalDate(report.date!.timestamp)}-report.xlsx`);
    });
  }

  speedFormatter(speed: number) {
    return Math.round(speed * 100) / 100;
  }

}
